import { Navigate, Outlet, useRoutes } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ROUTES } from '../constants/routes';
import { DoctorSessionProvider, useDoctorSession } from './contexts/DoctorSessionContext';
import DoctorProductTour from './components/DoctorProductTour';
import DoctorFeedbackModal from './components/DoctorFeedbackModal';
import DoctorSignupScreen from './screens/DoctorSignupScreen';
import DoctorLoginScreen from './screens/DoctorLoginScreen';
import DoctorPatientsScreen from './screens/DoctorPatientsScreen';
import DoctorPatientFormScreen from './screens/DoctorPatientFormScreen';
import DoctorPatientDetailScreen from './screens/DoctorPatientDetailScreen';
import DoctorGeneratingScreen from './screens/DoctorGeneratingScreen';
import DoctorReviewScreen from './screens/DoctorReviewScreen';
import DoctorNotesScreen from './screens/DoctorNotesScreen';
import DoctorPaywallScreen from './screens/DoctorPaywallScreen';
import DoctorSettingsScreen from './screens/DoctorSettingsScreen';

function DoctorProtected() {
  const { isAuthenticated, accountType } = useAuth();
  const { loading } = useDoctorSession();

  if (!isAuthenticated || accountType !== 'doctor') {
    return <Navigate to={ROUTES.DOCTOR_LOGIN} replace />;
  }
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-gray-400">
        …
      </div>
    );
  }
  return (
    <>
      <Outlet />
      <DoctorProductTour />
      <DoctorFeedbackModal />
    </>
  );
}

function DoctorAppRoutes() {
  const { isAuthenticated, accountType } = useAuth();
  const authedDoctor = isAuthenticated && accountType === 'doctor';

  return useRoutes([
    {
      path: '/doctor/signup',
      element: <DoctorSignupScreen />,
    },
    {
      path: '/doctor/login',
      element: authedDoctor ? <Navigate to={ROUTES.DOCTOR_PATIENTS} replace /> : <DoctorLoginScreen />,
    },
    {
      path: '/doctor/onboarding',
      element: <Navigate to={ROUTES.DOCTOR_PATIENTS} replace />,
    },
    {
      path: '/doctor',
      element: <DoctorProtected />,
      children: [
        { index: true, element: <Navigate to={ROUTES.DOCTOR_PATIENTS} replace /> },
        { path: 'dashboard', element: <Navigate to={ROUTES.DOCTOR_PATIENTS} replace /> },
        { path: 'patients', element: <DoctorPatientsScreen /> },
        { path: 'patients/new', element: <DoctorPatientFormScreen mode="create" /> },
        { path: 'patients/:id', element: <DoctorPatientDetailScreen /> },
        { path: 'patients/:id/edit', element: <DoctorPatientFormScreen mode="edit" /> },
        { path: 'patients/:id/generating', element: <DoctorGeneratingScreen /> },
        { path: 'patients/:id/review', element: <DoctorReviewScreen /> },
        { path: 'notes', element: <DoctorNotesScreen /> },
        { path: 'notes/:noteId', element: <DoctorReviewScreen /> },
        { path: 'paywall', element: <DoctorPaywallScreen /> },
        { path: 'settings', element: <DoctorSettingsScreen /> },
        { path: '*', element: <Navigate to={ROUTES.DOCTOR_PATIENTS} replace /> },
      ],
    },
  ]);
}

export default function DoctorApp() {
  return (
    <DoctorSessionProvider>
      <DoctorAppRoutes />
    </DoctorSessionProvider>
  );
}
